import chroma from 'chroma-js';
import { useAtomValue } from 'jotai';
import moment from 'moment';
import React, { useState } from 'react';
import { Collapse } from 'reactstrap';

import { getDrillDownUrl } from './helper';
import {
  historyChartDataAtom,
  LastSyncDateAtom,
  historyChartLabelsAtom,
  showHistoryChartCountsAtom,
  intervalAtom,
  policyIdFilterAtom,
  criticalityFiltersAtom,
  appliedHostIdsAtom,
} from './state';
import HistoryChart from '../components/HistoryChart';
import { getSeverityFilterValues } from '../helper';
import { IntervalFilter } from '../types';

import '../styles.css';

type Props = {
  setModalComponentData: (ModalDialogData) => void;
};

const HistoryCharts = ({ setModalComponentData }: Props) => {
  const policyId = useAtomValue(policyIdFilterAtom);
  const severityFilters = useAtomValue(criticalityFiltersAtom);
  const lastSyncDate = useAtomValue(LastSyncDateAtom);
  const historyChartLabels = useAtomValue(historyChartLabelsAtom);
  const historyChartData = useAtomValue(historyChartDataAtom);
  const showHistoryChartCounts = useAtomValue(showHistoryChartCountsAtom);
  const interval = useAtomValue(intervalAtom);
  const appliedHostIds = useAtomValue(appliedHostIdsAtom);

  const [expandedChartKey, setExpandedChartKey] = useState<string | null>(null);

  const toggleChartExpansion = (chartKey: string) => {
    setExpandedChartKey((prevKey) => (prevKey === chartKey ? null : chartKey));
  };

  function getChartTitleDate(prefix: string) {
    const lastLabelDate = historyChartLabels[historyChartLabels.length - 1];
    if (!lastLabelDate) return prefix;

    const lastLabelMoment = moment(lastLabelDate, 'MM/DD');
    const result = lastLabelMoment.format('MMM D');

    return `${prefix} (as of ${result})`;
  }

  function getChartTitle(prefix: string) {
    switch (interval) {
      case IntervalFilter.Week:
      case IntervalFilter.FourWeeks:
      case IntervalFilter.ThreeMonth:
      case IntervalFilter.SixMonth:
        return getChartTitleDate(prefix);
      default:
        return prefix;
    }
  }

  function getHistoryDrilldownUrl(status?: string) {
    return getDrillDownUrl({
      policyId: policyId,
      status_in: status ? [status] : undefined,
      criticality_in: getSeverityFilterValues(severityFilters),
      hosts_in: appliedHostIds,
    });
    return '';
  }

  const colorScale = (value) =>
    chroma
      .scale([
        '#8B0000', // Dark Red
        '#B22222', // Firebrick
        '#FF4500', // OrangeRed
        '#FF6347', // Tomato
        '#FF7F50', // Coral
        '#32CD32', // LimeGreen
        '#228B22', // ForestGreen
        '#006400', // Dark Green
      ])
      .mode('lab')
      .domain([0, 14, 28, 42, 56, 70, 84, 100])(value)
      .hex();

  const complianceDataSet = historyChartData?.slice(3, 4);

  const getChartPropertiesDictionary = () => ({
    all: {
      title: 'Findings',
      labels: historyChartLabels,
      tooltipLabels: [
        { label: 'Passed', hostsCount: null },
        { label: 'Failed', hostsCount: null },
      ],
      datasets: historyChartData?.slice(0, 2),
      colors: ['#109307', '#ff1716'],
      selectedInterval: interval,
      showCounts: showHistoryChartCounts,
      onExpandClick: () => toggleChartExpansion('all'),
      getDrillDownUrl: (sourceType) => getHistoryDrilldownUrl(sourceType), // Restore this attribute
      newDataGenerationIndex: 0,
    },
    assets: {
      title: 'Assets',
      labels: historyChartLabels,
      tooltipLabels: [{ label: 'Assets', hostsCount: null }],
      // if more then 1 appliedHostIds then dont show, else show only the first one
      datasets: appliedHostIds?.length > 1 ? [] : historyChartData?.slice(2, 3),
      colors: ['#0096FF'],
      selectedInterval: interval,
      showCounts: showHistoryChartCounts,
      onExpandClick: () => toggleChartExpansion('assets'),
      getDrillDownUrl: () => getHistoryDrilldownUrl(), // Restore this attribute
      newDataGenerationIndex: 0,
    },
    compliance: {
      title: '% Compliance Score',
      labels: historyChartLabels,
      tooltipLabels: [{ label: 'Compliance Score', hostsCount: null }],
      datasets: complianceDataSet,
      colors:
        complianceDataSet && complianceDataSet.length > 0
          ? complianceDataSet.map((dataset) => {
              if (!dataset || dataset.length === 0) {
                return '#D3D3D3'; // Default color (Light Gray) if dataset is empty
              }
              const lastValue = dataset[dataset.length - 1];
              return colorScale(lastValue);
            })
          : ['#D3D3D3'], // Default colors array when complianceDataSet is empty
      colorScale,
      selectedInterval: interval,
      showCounts: showHistoryChartCounts,
      onExpandClick: () => toggleChartExpansion('compliance'),
      getDrillDownUrl: () => getHistoryDrilldownUrl(), // Restore this attribute
      newDataGenerationIndex: 0,
    },
  });

  let chartPropertiesDictionary;
  if (historyChartData) {
    chartPropertiesDictionary = getChartPropertiesDictionary();
  }

  return (
    <>
      {historyChartData && (
        <div className="row">
          <Collapse isOpen={!!expandedChartKey} className="col-12">
            {expandedChartKey && (
              <HistoryChart
                key="expandedChart"
                {...chartPropertiesDictionary[expandedChartKey]}
                isActive={true}
              />
            )}
          </Collapse>
          <div className="col-md-6">
            <HistoryChart
              {...chartPropertiesDictionary['all']}
              isActive={expandedChartKey === 'all'}
            />
          </div>
          {!(appliedHostIds?.length > 1) && (
            <div className="col-md-3">
              <HistoryChart
                {...chartPropertiesDictionary['assets']}
                isActive={expandedChartKey === 'assets'}
              />
            </div>
          )}
          <div className="col-md-3">
            <HistoryChart
              {...chartPropertiesDictionary['compliance']}
              isActive={expandedChartKey === 'compliance'}
            />
          </div>
        </div>
      )}
    </>
  );
};

export default HistoryCharts;
